<template>
  <div :class="['comm flex', directionClass]">
    <div :class="['comm--item', directionClass, {'reverse': isInbound}]">
      <!--The Comm-->
      <div class="flex-grow" style="max-width: 90%">
        <!--Date-->
        <div :class="['flex flex-between', {'flex-row-reverse': isOutbound}]">
          <p class="comm--item--date">{{ date }}</p>

          <p
            v-if="isScheduled"
            @click="cancelScheduledComm()"
            class="comm--item--unscheduled"
          >DON'T SEND</p>

          <p
            v-if="isNotAddressed"
            class="comm--item--not_addressed"
          >Not Addressed</p>
        </div>

        <!--Body-->
        <div :class="['comm--item--body', directionClass]">
          <p class="comm--item--main_message">
            <sensitive-data-wrapper>
              <template v-if="isCallComm">
                <template v-if="isVideoCall">Video </template>Call {{ isInbound ? "from" : "to" }} {{ formattedContactNumber }}
              </template>
            </sensitive-data-wrapper>
            <template v-if="isTextComm">{{ comm.text_message.replace(/<[^>]*>?/gm, "") }}</template>
            <template v-if="isEmailComm">{{ comm.email_subject }}</template>
          </p>
          <p class="comm--item--sub_message">
            <template v-if="isCallComm">
              <sensitive-data-wrapper>
                <span class="is-block is-size-7 mb-25">{{ isInbound ? 'To' : 'From'}}: {{ formattedTrackingNumber }}</span>
              </sensitive-data-wrapper>

              <span class="mr-50">{{ duration }}</span>
              <template v-if="recordingURL">
                <audio v-if="!isVideoCall" controls>
                  <source :src="recordingURL">
                </audio>
                <video v-if="isVideoCall" width="320" height="240" controls>
                  <source :src="recordingURL" type="video/mp4">
                  Your browser does not support the video tag.
                </video>
              </template>
              <template v-else>
                <button-link
                  @click="getRecording"
                  :loading="recordingLoading"
                >check for recording</button-link>

                <span v-if="recordingUrlMessage">{{ recordingUrlMessage }}</span>
              </template>
            </template>

            <template v-if="isTextComm">
              <sensitive-data-wrapper>
                <span class="is-block mb-25">{{ isInbound ? 'From' : 'To'}}: {{ comm.contact_name ? comm.contact_name + " " : "" }} {{ formattedContactNumber}}</span>
                <span class="is-block">{{ isInbound ? 'To' : 'From'}}: {{ formattedTrackingNumber }}</span>
              </sensitive-data-wrapper>
            </template>

            <template v-if="isEmailComm">
              <button-link
                @click="$refs.emailModal.open()"
              >view email
              </button-link>
            </template>
          </p>
        </div>

        <div class="comm-item--error" v-if="comm.error_message">
          <p
            class="comm--item--error_message"
          >Message not sent: {{ comm.error_message }}</p>
        </div>
      </div>

      <!-- Icons (optional) -->
      <div class="comm--item--icons" v-if="showIconColumn">
        <icon-default
          v-if="showTypeIcons"
          :icon="iconType"
          size="is-small"
          :color="iconColor"
          class="mr-50"
        ></icon-default>

        <app-avatar
          v-if="displayCreatedByIcon && isCreatedByUser"
          :user-id="comm.createdBy.id"
          :size="16"
          position="is-left"
        ></app-avatar>

        <icon-default
          v-if="displayCreatedByIcon && isCreatedByAutomation"
          icon="automation"
          color="is-secondary"
          size="is-small"
        ></icon-default>

        <icon-default
          v-if="displayCreatedByIcon && isCreatedByAppointment"
          icon="appointment"
          color="is-secondary"
          size="is-small"
        ></icon-default>

        <icon-default
          v-if="displayCreatedByIcon && isCreatedByFollowUp"
          icon="follow-up"
          color="is-secondary"
          size="is-small"
        ></icon-default>

        <icon-default
          v-if="displayCreatedByIcon && isCreatedByWebhook"
          icon="webhook"
          color="is-secondary"
          size="is-small"
        ></icon-default>
      </div>
    </div>

    <modal-container>
      <modal-default
        ref="emailModal"
        title=""
        is-wide
      >
        <email-preview
          :email="comm"
          hide-lead-link
        ></email-preview>
      </modal-default>
    </modal-container>
  </div>
</template>

<script>
import {ENUMS} from "../../../services/enums";
import {MODELS} from "../../../services/models";
import EmailPreview from "./EmailPreview";
import dates from "../../../services/dates";
import SensitiveDataWrapper from "../../SensitiveDataWrapper.vue";

export default {
  name: "CommLogItem",
  components: {SensitiveDataWrapper, EmailPreview},
  props: {
    comm: {
      type: Object,
      required: true
    },
    showTypeIcons: {
      type: Boolean,
      default: false
    },
    showUserAvatar: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      recordingURL: null,
      recordingUrlMessage: null,
      recordingLoading: false,
    };
  },
  computed: {
    isVideoCall() {
      return this.comm.type === ENUMS.CommunicationTypeEnum.VideoCall.value;
    },
    isCallComm() {
      return this.comm.type === ENUMS.CommunicationTypeEnum.Call.value;
    },
    isTextComm() {
      return this.comm.type === ENUMS.CommunicationTypeEnum.Text.value;
    },
    isEmailComm() {
      return this.comm.type === ENUMS.CommunicationTypeEnum.Email.value;
    },
    isInbound() {
      return this.comm.communication_direction === ENUMS.CommunicationDirectionEnum.Inbound.value;
    },
    isOutbound() {
      return this.comm.communication_direction === ENUMS.CommunicationDirectionEnum.Outbound.value;
    },
    formattedNumber() {
      if (this.isInbound) return this.$utils.formatNumber(this.comm.contact_number);
      if (this.isOutbound) return this.$utils.formatNumber(this.comm.tracking_number);
    },
    formattedContactNumber() {
      return this.$utils.formatNumber(this.comm.contact_number);
    },
    formattedTrackingNumber() {
      return this.$utils.formatNumber(this.comm.tracking_number);
    },
    textLineItem() {
      const prefix = this.isInbound ? "From: " : "To: ";
      const name = this.isInbound && this.comm.contact_name ? this.comm.contact_name + " " : "";

      return prefix + name + this.formattedNumber
    },
    date() {
      return dates.formatAsDateTime(this.comm.scheduled_at);
    },
    duration() {
      return this.$utils.formatDuration(this.comm.duration_in_seconds);
    },
    isScheduled() {
      return this.comm.is_scheduled;
    },
    isNotAddressed() {
      return this.isInbound && !this.comm.addressed_at;
    },
    directionClass() {
      if (this.isInbound) return "inbound";
      if (this.isOutbound) return "outbound";
      return "";
    },
    iconColor() {
      if (this.isCallComm) return "is-call";
      if (this.isTextComm) return "is-text";
      if (this.isEmailComm) return "is-email";
    },
    iconType() {
      if (this.isCallComm) return "call";
      if (this.isTextComm) return "text";
      if (this.isEmailComm) return "email";
    },
    showIconColumn() {
      // inbound and showTypeIcons
      if (this.showTypeIcons) return true;

      // outbound and displayUserAvatar
      return !!(this.isOutbound && this.displayUserAvatar);
    },
    displayCreatedByIcon() {
      return this.showUserAvatar && this.comm.communication_direction === ENUMS.CommunicationDirectionEnum.Outbound.value;
    },
    isCreatedByUser() {
      return this.comm.createdBy.class_name === MODELS.User.class_name;
    },
    isCreatedByAppointment() {
      return this.comm.createdBy.class_name === MODELS.AppointmentReminderAttendee.class_name;
    },
    isCreatedByAutomation() {
      return this.comm.createdBy.class_name === MODELS.EmailAutomation.class_name
        || this.comm.createdBy.class_name === MODELS.TextAutomation.class_name;
    },
    isCreatedByFollowUp() {
      return this.comm.createdBy.class_name === MODELS.FollowUpSequenceStepTemplate.class_name;
    },
    isCreatedByWebhook() {
      return this.comm.createdBy.class_name === MODELS.Webhook.class_name;
    },
  },
  methods: {
    cancelScheduledComm() {
      this.$utils.confirm(
        `Are you sure you want to <strong>cancel</strong> the scheduled text?`,
        "Cancel Scheduled Text"
      )
        .then(() => {
          this.$api.communicationLogs.cancelSchedule(this.comm.id).then(res => {
          });
        });
    },
    getRecording() {
      this.recordingLoading = true;
      this.$forceUpdate();

      this.$api.communicationLogs.getRecording(this.comm.id).then(response => {
        if (response.data.url) {
          this.recordingURL = response.data.url;
        } else {
          this.recordingUrlMessage = 'no recording'
        }

        this.recordingLoading = false;
        this.$forceUpdate();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.comm {
  margin-bottom: 0.75rem;

  &.inbound {
    justify-content: flex-start;
  }

  &.outbound {
    justify-content: flex-end;
  }

  &--item {
    display: flex;
    min-width: 260px;

    &.reverse {
      flex-direction: row-reverse;
    }

    &.inbound {
      justify-content: flex-start;
    }

    &.outbound {
      justify-content: flex-end;
    }

    &--date {
      margin-bottom: 2px;
      font-size: 0.75rem;
      color: $text-light;

      small {
        color: $text;
      }
    }

    &--unscheduled {
      font-size: 0.625rem;
      color: $link;
      cursor: pointer;

      &:hover {
        color: $link-hover;
      }
    }

    &--not_addressed {
      position: relative;
      font-size: 0.625rem;
      color: $text-muted;

      &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: 100%;
        height: 8px;
        width: 8px;
        margin-left: 4px;
        background-color: $primary;
        border-radius: 100%;
      }
    }

    &--body {
      padding: 6px;

      &.inbound {
        background-color: transparentize($grey-50, 0.5);
      }

      &.outbound {
        background-color: $blue-50;
      }
    }

    &--main_message {
      margin-bottom: 4px;
      font-size: 0.875rem;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    &--sub_message {
      font-size: 0.75rem;
      color: $text-light;
    }

    &--error_message {
      margin-top: 3px;
      font-size: 0.625rem;
      color: $red;
    }

    &--icons {
      width: 20px;
      //padding-top: calc(0.75rem + 2px);
      margin-left: 6px;
    }
  }
}
</style>
